import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import withTooltip from '@hoc/withTooltip';
import withDropDownMenu from '@hoc/withDropDownMenu';
import { whenEnterButtonPressed } from '@utils/interactionEvents';
import { Wrapper, Content, Icon, NotificationIcon } from './IconButton.styled';

export const ICON_BUTTON_VARIANT_PRIMARY = 'primary';
export const ICON_BUTTON_VARIANT_ERROR = 'error';
export const ICON_BUTTON_VARIANT_OUTLINE = 'outline';
export const ICON_BUTTON_VARIANT_STANDARD = 'standard';
export const ICON_BUTTON_VARIANT_TONAL = 'tonal';
export const ICON_BUTTON_VARIANT_PLAIN = 'plain';

export const ICON_BUTTON_NOTIFICATION_ICON_VARIANT_CAUTION = 'caution';
export const ICON_BUTTON_NOTIFICATION_ICON_VARIANT_ERROR = 'error';
export const ICON_BUTTON_NOTIFICATION_ICON_VARIANT_SUCCESS = 'success';

const WrapperWithTooltip = withTooltip(Wrapper);

const IconButton = forwardRef(
  (
    {
      className,
      dataTestId = 'icon-button',
      disabled,
      iconColor,
      iconName,
      loading,
      onClick,
      renderAs,
      selected,
      submitType,
      tabIndex,
      title,
      tooltip = {},
      variant = ICON_BUTTON_VARIANT_STANDARD,
      withNotificationIcon,
      notificationIconVariant = ICON_BUTTON_NOTIFICATION_ICON_VARIANT_ERROR,
    },
    ref,
  ) => {
    const disableActions = disabled || loading;

    const WrapperComponent = useMemo(
      () => (isEmpty(tooltip) ? Wrapper : WrapperWithTooltip),
      [tooltip],
    );

    const notificationIconColorName = useMemo(() => {
      switch (notificationIconVariant) {
        case ICON_BUTTON_NOTIFICATION_ICON_VARIANT_CAUTION:
          return 'caution';
        case ICON_BUTTON_NOTIFICATION_ICON_VARIANT_ERROR:
          return 'error';
        case ICON_BUTTON_NOTIFICATION_ICON_VARIANT_SUCCESS:
          return 'success';
        default:
          return 'error';
      }
    }, [notificationIconVariant]);

    return (
      <WrapperComponent
        {...tooltip}
        as={renderAs}
        ref={ref}
        type={submitType ? 'submit' : 'button'}
        className={className}
        data-testid={dataTestId}
        disabled={disabled}
        $loading={loading}
        onClick={disableActions ? undefined : onClick}
        onKeyDown={renderAs ? whenEnterButtonPressed(onClick) : undefined}
        $selected={selected}
        tabIndex={disableActions ? -1 : tabIndex}
        title={title}
        $variant={variant}
      >
        {withNotificationIcon && (
          <NotificationIcon
            $iconColor={notificationIconColorName}
            name="Dot_s_1"
          />
        )}

        <Content
          $loading={loading}
          $disabled={disabled}
          $selected={selected}
          $variant={variant}
        >
          <Icon
            $loading={loading}
            $disabled={disabled}
            $iconColor={iconColor}
            name={loading ? 'progress_activity_0' : iconName}
            $selected={selected}
            $variant={variant}
          />
        </Content>
      </WrapperComponent>
    );
  },
);

IconButton.propTypes = {
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  iconColor: PropTypes.string,
  iconName: PropTypes.string,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  renderAs: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  selected: PropTypes.bool,
  submitType: PropTypes.bool,
  tabIndex: PropTypes.number,
  title: PropTypes.string,
  tooltip: PropTypes.object,
  variant: PropTypes.oneOf([
    ICON_BUTTON_VARIANT_OUTLINE,
    ICON_BUTTON_VARIANT_PLAIN,
    ICON_BUTTON_VARIANT_PRIMARY,
    ICON_BUTTON_VARIANT_ERROR,
    ICON_BUTTON_VARIANT_STANDARD,
    ICON_BUTTON_VARIANT_TONAL,
  ]),
  withNotificationIcon: PropTypes.bool,
  notificationIconVariant: PropTypes.oneOf([
    ICON_BUTTON_NOTIFICATION_ICON_VARIANT_CAUTION,
    ICON_BUTTON_NOTIFICATION_ICON_VARIANT_ERROR,
    ICON_BUTTON_NOTIFICATION_ICON_VARIANT_SUCCESS,
  ]),
};

IconButton.displayName = 'IconButton';

export default withDropDownMenu(IconButton);
